// Constants
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
var BreakerState = /*#__PURE__*/ function(BreakerState) {
    BreakerState["Closed"] = "CLOSED";
    BreakerState["Open"] = "OPEN";
    return BreakerState;
}(BreakerState || {});
/**
 * IAuthService decorator that bounds repeated failures to the same per-scopes key.
 *
 * After `circuitBreakerThreshold` consecutive failures for a key, the breaker OPENs and
 * rejects all subsequent calls for that key with the last observed error, without invoking
 * the wrapped service. After `circuitBreakerCooldownMs` the breaker auto-resets to CLOSED.
 *
 * No probe, no internal retry. Just back-pressure. Disabled when changegate
 * DisableAuthCircuitBreaker is true or when circuitBreakerThreshold <= 0.
 *
 * Per-key isolation: failures on graph don't open the breaker for trouter. Claims are
 * intentionally NOT part of the key — a CAE-challenged token shares a breaker with its
 * un-challenged counterpart for the same scopes; the claims dimension is captured only
 * in the `HasClaims` telemetry field, never in the breaker state.
 */ export class CircuitBreakerAuthService {
    async fetchAccessTokenAsync(parameters) {
        const config = this.configProvider();
        const settings = config.settings.auth;
        const { changeGates } = config;
        if (changeGates.DisableAuthCircuitBreaker || settings.circuitBreakerThreshold <= 0) {
            return this.inner.fetchAccessTokenAsync(parameters);
        }
        const key = CircuitBreakerAuthService.getKey(parameters);
        const hasClaims = !!parameters.claims;
        const entry = this.getOrCreate(key);
        if (entry.state === "OPEN") {
            this.loggers.traceLogger.logTrace(0x1e0937b0 /* TODO(tag-mint): tag_4ct60 — auth breaker OPEN, fail fast */ , TraceLevel.Warning, `Auth circuit breaker OPEN — failing fast [Key=${key}][HasClaims=${hasClaims}]`);
            return Promise.reject(entry.cachedError);
        }
        try {
            const result = await this.inner.fetchAccessTokenAsync(parameters);
            entry.failureCount = 0;
            return result;
        } catch (error) {
            entry.cachedError = error;
            entry.failureCount += 1;
            this.loggers.traceLogger.logTrace(0x1e0937b1 /* TODO(tag-mint): tag_4ct61 — auth fetch failed */ , TraceLevel.Warning, `Auth fetch failed [Key=${key}][HasClaims=${hasClaims}][ConsecutiveFailures=${entry.failureCount}][Threshold=${settings.circuitBreakerThreshold}]`);
            if (entry.failureCount >= settings.circuitBreakerThreshold) {
                this.open(key, entry, hasClaims, settings.circuitBreakerCooldownMs);
            }
            throw error;
        }
    }
    getOrCreate(key) {
        let entry = this.entries.get(key);
        if (entry == null) {
            entry = {
                state: "CLOSED",
                failureCount: 0,
                cachedError: null,
                cooldownTimer: null
            };
            this.entries.set(key, entry);
        }
        return entry;
    }
    open(key, entry, hasClaims, cooldownMs) {
        if (entry.state === "OPEN") {
            // Already open — do not reset the cooldown timer (concurrency guard).
            return;
        }
        entry.state = "OPEN";
        this.loggers.traceLogger.logTrace(0x1e0937b2 /* TODO(tag-mint): tag_4ct62 — auth breaker → OPEN */ , TraceLevel.Warning, `Auth circuit breaker → OPEN [Key=${key}][HasClaims=${hasClaims}][CooldownMs=${cooldownMs}]`);
        entry.cooldownTimer = setTimeout(()=>{
            entry.state = "CLOSED";
            entry.failureCount = 0;
            entry.cooldownTimer = null;
            this.loggers.traceLogger.logTrace(0x1e0937b3 /* TODO(tag-mint): tag_4ct63 — auth breaker cooldown expired */ , TraceLevel.Info, `Auth circuit breaker cooldown expired — CLOSED [Key=${key}]`);
        }, cooldownMs);
    }
    /**
     * Derive a stable per-scopes key. Resource form is normalized to the equivalent
     * scopes form. Scopes are joined verbatim (no sort).
     *
     * Claims are NOT part of the key — a CAE-challenged variant and its un-challenged
     * counterpart share a single breaker. The `claims` dimension is captured in the
     * `HasClaims` trace field so it can still be sliced in telemetry.
     */ static getKey(p) {
        const scopes = "scopes" in p ? p.scopes : [
            `${p.resource}/.default`
        ];
        return scopes.join("|");
    }
    constructor(inner, configProvider, loggers){
        this.inner = inner;
        this.configProvider = configProvider;
        this.loggers = loggers;
        this.entries = new Map();
    }
}
